/**
 * ProductScout Service
 *
 * REST API service for the ProductScout AI assistant. Thin wrapper over
 * `RestApi.request(...)`; matches the pattern used by
 * `LiveLeaderboard.service.js` (thin wrapper, default-exported method map)
 * and `SalesTrackerClient.service.js` (endpoint-override for sub-resources).
 *
 * Response shapes preserve snake_case verbatim from
 * `docs/modules/ProductScout/api-reference.md` so the store can consume
 * them without a conversion layer.
 *
 * `sendMessage` returns the full assistant message in the HTTP response
 * (synchronous broadcaster path). The same turn is also broadcast as
 * MessageDelta + MessageCompleted on the user's private Reverb channel
 * `productscout.message.{userId}`; the store dedupes via message_id so
 * either delivery order is safe (HTTP-first or events-first).
 */

import RestApi from './../../api/automation-api-client'

const conversationsEndpoint = '/productscout/conversations'
const messagesEndpoint = '/productscout/messages'
const usageEndpoint = '/productscout/usage'

export default {
  /**
   * Create a new conversation; backend archives any existing active one.
   * Endpoint: POST /api/productscout/conversations
   *
   * @param {Object} intake snake_case intake payload (state, age_band,
   *   gender, tobacco, conditions, coverage_interest, notes).
   * @returns {Promise<{conversation:Object, disclaimer:Object,
   *   initial_message:Object|null, channel:string}>}
   */
  startConversation: async intake => RestApi.request('POST', conversationsEndpoint, null, { intake }),

  /**
   * Send a user message; backend dispatches the streaming broadcaster.
   * HTTP returns the canonical user/assistant rows + usage. Reverb
   * delivers token deltas + completion on the user's private channel.
   * Endpoint: POST /api/productscout/conversations/{conversation_id}/messages
   *
   * @param {number} conversation_id
   * @param {string} content
   * @returns {Promise<{user_message:Object, assistant_message:Object,
   *   usage:Object}>}
   */
  sendMessage: async (conversation_id, content) =>
    RestApi.request(
      'POST',
      conversationsEndpoint,
      null,
      { content },
      { endpoint: `${conversationsEndpoint}/${conversation_id}/messages` }
    ),

  /**
   * Ask for the intake-driven opening reply (ProductScout answers first).
   * Synchronous like sendMessage; Reverb also streams it.
   * Endpoint: POST /api/productscout/conversations/{conversation_id}/open
   *
   * @param {number} conversation_id
   * @returns {Promise<{assistant_message:Object|null, usage?:Object}>}
   */
  openConversation: async conversation_id =>
    RestApi.request('POST', conversationsEndpoint, null, null, {
      endpoint: `${conversationsEndpoint}/${conversation_id}/open`,
    }),

  /**
   * Fetch a conversation and its full message timeline.
   * Endpoint: GET /api/productscout/conversations/{conversation_id}
   *
   * @param {number} conversation_id
   * @returns {Promise<{conversation:Object, messages:Object[]}>}
   */
  getConversation: async conversation_id => RestApi.request('GET', conversationsEndpoint, conversation_id),

  /**
   * List the user's conversations, most-recent first.
   * Endpoint: GET /api/productscout/conversations?per_page=20&page=1
   *
   * @param {{per_page?:number, page?:number}} [opts]
   * @returns {Promise<{data:Object[], meta:Object}>}
   */
  listConversations: async (opts = {}) =>
    RestApi.request('GET', conversationsEndpoint, null, {
      query: { per_page: opts.per_page, page: opts.page },
    }),

  /**
   * Archive (close) a conversation. Idempotent; no data is deleted.
   * Endpoint: POST /api/productscout/conversations/{id}/archive
   *
   * @param {number} conversation_id
   * @returns {Promise<{conversation_id:number, status:string,
   *   ended_at:string, ended_reason:string}>}
   */
  archive: async conversation_id =>
    RestApi.request('POST', conversationsEndpoint, null, null, {
      endpoint: `${conversationsEndpoint}/${conversation_id}/archive`,
    }),

  /**
   * Flag an assistant message for engineering review.
   * Endpoint: POST /api/productscout/messages/{message_id}/flag
   *
   * @param {number|string} message_id
   * @param {string} reason
   * @returns {Promise<{message_id:number|string, flagged_at:string,
   *   flagged_reason:string}>}
   */
  flagMessage: async (message_id, reason) =>
    RestApi.request('POST', messagesEndpoint, null, { reason }, { endpoint: `${messagesEndpoint}/${message_id}/flag` }),

  /**
   * Daily + monthly spend for the in-sidebar budget indicator.
   * Endpoint: GET /api/productscout/usage
   *
   * @returns {Promise<{today:Object, month_to_date:Object}>}
   */
  getUsage: async () => RestApi.request('GET', usageEndpoint),
}
